import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SigningField } from '../types/fields';
import { getCompactTextFieldSizeInPoints } from '../utils/coordinates';
import { resolveFieldTextStyle, toPreviewCss } from '../utils/fontMapping';
import { fieldTextLayoutMode, formLineBaselineOffsetFromBottom } from '../utils/textLayout';
import { measureTextFieldScreenBox } from '../utils/textFieldMeasure';

export interface EditorScreenBox {
  width: number;
  height: number;
  displayScale: number;
}

interface InlineFieldEditorProps {
  field: SigningField;
  screenRect: { top: number; left: number; width: number; height: number };
  scale: number;
  onCommit: (value: string, editorBox: EditorScreenBox) => void;
  onCancel: () => void;
  /** Set by parent before unmounting via global Escape so blur does not commit. */
  parentDismissRef?: React.MutableRefObject<boolean>;
}

const EDITABLE_TYPES = new Set(['text', 'name', 'date', 'signature-type', 'initials', 'text-bubble']);

export function isInlineEditableField(field: SigningField | null): boolean {
  return !!field && EDITABLE_TYPES.has(field.type);
}

/**
 * Overlay textarea for quick in-place editing (click, double-click, or Enter on a text field).
 * Box size tracks the text dynamically so the editor hugs the content.
 */
export const InlineFieldEditor: React.FC<InlineFieldEditorProps> = ({
  field,
  screenRect,
  scale,
  onCommit,
  onCancel,
  parentDismissRef,
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const cancelledRef = useRef(false);
  // BUG-03: capture initial value at open so Escape can revert cleanly
  const initialValueRef = useRef(field.value || '');
  const isMultiline = field.type === 'text' || field.type === 'text-bubble';

  const layoutMode = fieldTextLayoutMode(field.type, {
    boxHeight: field.height,
    value: field.value,
  });
  const style = resolveFieldTextStyle(field, layoutMode);
  const css = toPreviewCss(style, scale);
  const anchorBottom = layoutMode === 'form-line';
  const screenBottom = screenRect.top + screenRect.height;
  const padX = 4 * scale;

  const [editorBox, setEditorBox] = useState<EditorScreenBox>(() =>
    measureTextFieldScreenBox(field, field.value || '', scale)
  );

  const syncBoxToText = useCallback(
    (value: string) => {
      const measured = measureTextFieldScreenBox(field, value, scale);
      setEditorBox(measured);

      requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const scrollW = Math.ceil(el.scrollWidth + padX * 2);
        const scrollH = Math.ceil(el.scrollHeight + 4);
        setEditorBox((prev) => ({
          width: Math.max(measured.width, scrollW, prev.width),
          height: Math.max(measured.height, scrollH, prev.height),
          displayScale: scale,
        }));
        if (isMultiline) {
          el.style.height = 'auto';
          el.style.height = `${Math.max(measured.height, el.scrollHeight)}px`;
        }
      });
    },
    [field, isMultiline, padX, scale]
  );

  useLayoutEffect(() => {
    syncBoxToText(field.value || '');
  }, [field.id, field.fontSize, field.fontFamily, field.bold, field.italic, scale, syncBoxToText, field.value]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // BUG-03: reset initial value ref each time the editor opens for a new field
    initialValueRef.current = field.value || '';
    el.focus();
    el.select();
    syncBoxToText(el.value);
  }, [field.id, syncBoxToText, field.value]);

  const commit = () => {
    const el = ref.current;
    const raw = el?.value ?? '';
    // BUG-09: only trim for date/initials; preserve spaces in free text fields
    const value =
      field.type === 'date' || field.type === 'initials' ? raw.trim() : raw;
    const box: EditorScreenBox = {
      width: el
        ? Math.ceil(Math.max(editorBox.width, el.scrollWidth + padX * 2))
        : editorBox.width,
      height: el
        ? Math.ceil(Math.max(editorBox.height, el.scrollHeight + 4))
        : editorBox.height,
      displayScale: scale,
    };
    onCommit(value, box);
  };

  const top = anchorBottom ? screenBottom - editorBox.height : screenRect.top;
  const fontSizePx = parseInt(css.fontSize, 10) || Math.round(style.fontSize * scale);
  const lineHeightPx = fontSizePx * style.lineHeight;
  const padTop = anchorBottom
    ? Math.max(2, editorBox.height - lineHeightPx - 2 * scale)
    : 2 * scale;

  return (
    <textarea
      ref={ref}
      className={`inline-field-editor ${anchorBottom ? 'inline-field-editor-form-line' : ''}`}
      defaultValue={field.value || ''}
      rows={isMultiline ? Math.max(2, Math.ceil((field.value?.split('\n').length || 1))) : 1}
      wrap={isMultiline ? 'soft' : 'off'}
      style={{
        position: 'absolute',
        left: screenRect.left,
        top,
        width: editorBox.width,
        minWidth: editorBox.width,
        height: isMultiline ? undefined : editorBox.height,
        minHeight: editorBox.height,
        fontSize: css.fontSize,
        fontFamily: css.fontFamily,
        fontStyle: css.fontStyle,
        fontWeight: css.fontWeight,
        textDecoration: css.textDecoration,
        textAlign: css.textAlign,
        letterSpacing: css.letterSpacing,
        lineHeight: css.lineHeight,
        padding: `${padTop}px ${padX}px 2px`,
        margin: 0,
        border: '1.5px solid var(--accent, var(--accent))',
        borderRadius: 3,
        // Field text is dark ink for the WHITE pdf page, so the editor stays on light
        // paper in both themes — a dark (var(--bg-panel)) backing hid the dark text.
        background: '#ffffff',
        color: css.color || '#0d0d1a',
        resize: 'none',
        overflowX: isMultiline ? 'hidden' : 'auto',
        overflowY: isMultiline ? 'auto' : 'hidden',
        zIndex: 120,
        boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
        boxSizing: 'border-box',
      }}
      placeholder={
        field.type === 'date' ? 'MM/DD/YYYY' : field.type === 'name' ? 'Printed name' : 'Type or paste text…'
      }
      onInput={(e) => syncBoxToText(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          cancelledRef.current = true;
          // BUG-03: revert textarea to its value at open time, then cancel
          if (ref.current) ref.current.value = initialValueRef.current;
          onCancel();
          return;
        }
        if (e.key === 'Enter' && (!isMultiline || (e.metaKey || e.ctrlKey))) {
          e.preventDefault();
          commit();
        }
      }}
      onBlur={() => {
        if (cancelledRef.current || parentDismissRef?.current) {
          cancelledRef.current = false;
          if (parentDismissRef) parentDismissRef.current = false;
          return;
        }
        commit();
      }}
      aria-label={`Edit ${field.type} field`}
    />
  );
};

/** Build update payload after inline edit (tight box sized to final text). */
export function buildTextFieldCommitUpdates(
  field: SigningField,
  value: string,
  editorScreenBox?: EditorScreenBox
): Partial<SigningField> {
  const updates: Partial<SigningField> = { value };

  let finalW: number | undefined;
  let finalH: number | undefined;

  if (editorScreenBox && editorScreenBox.displayScale > 0) {
    finalW = Math.max(18, Math.round(editorScreenBox.width / editorScreenBox.displayScale));
    finalH = Math.max(18, Math.round(editorScreenBox.height / editorScreenBox.displayScale));
  } else {
    const fs = field.fontSize ?? (field.type === 'signature-type' ? 16 : 13);
    const tightTypes = ['signature-type', 'date', 'name', 'initials'] as const;
    const shouldCompact =
      tightTypes.includes(field.type as (typeof tightTypes)[number]) ||
      (field.type === 'text' && value.trim() && !value.includes('\n'));

    if (shouldCompact) {
      const tight = getCompactTextFieldSizeInPoints(field.type, value, fs, field);
      finalW = tight.width;
      finalH = tight.height;
    }
  }

  if (finalW != null) updates.width = finalW;
  if (finalH != null) updates.height = finalH;

  // Anchor correction for 'text' (the only type whose layoutMode can flip between
  // 'form-line' and 'centered' on content edit). When the box size changes on commit
  // (esp. after "add text" + type), adjust y so the anchored edge (top for centered
  // paragraphs, baseline ref for short form-line) stays near the original placement
  // point instead of jumping because only w/h were updated before.
  if (field.type === 'text' && finalH != null) {
    const finalMode = fieldTextLayoutMode(field.type, { boxHeight: finalH, value });
    const oldTop = field.y + (field.height || 0);
    if (finalMode === 'form-line') {
      const fs = field.fontSize ?? 13;
      const sty = resolveFieldTextStyle({ ...field, value } as SigningField, finalMode);
      const fam = sty.metricsFamily || 'helvetica';
      const offset = formLineBaselineOffsetFromBottom(fs, fam);
      updates.y = Math.max(0, Math.round(oldTop - offset));
    } else {
      updates.y = Math.max(0, Math.round(oldTop - finalH));
    }
  }

  return updates;
}